import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

export type UserProfile = 'beginner' | 'expert' | 'exploring' | null;

export type TourId =
  | 'main'
  | 'editor'
  | 'hvac'
  | 'plant'
  | 'schedules'
  | 'geometry'
  | 'simulation';

interface OnboardingState {
  // Persisted state
  hasCompletedWelcome: boolean;
  userProfile: UserProfile;
  completedTours: TourId[];
  dismissedHints: string[];
  seenPages: string[];
  featureDiscoveries: string[];

  // Transient state (not persisted)
  activeTour: TourId | null;
  activeTourStep: number;
  isWelcomeOpen: boolean;
  activeHint: string | null;

  // Actions
  completeWelcome: (profile: UserProfile) => void;
  startTour: (tourId: TourId) => void;
  nextTourStep: () => void;
  prevTourStep: () => void;
  skipTour: () => void;
  completeTour: () => void;
  dismissHint: (hintId: string) => void;
  markPageSeen: (path: string) => void;
  discoverFeature: (featureId: string) => void;
  showHint: (hintId: string) => void;
  hideHint: () => void;
  resetOnboarding: () => void;
  openWelcome: () => void;
}

export const useOnboardingStore = create<OnboardingState>()(
  devtools(
    persist(
      (set) => ({
        // Persisted defaults
        hasCompletedWelcome: false,
        userProfile: null,
        completedTours: [],
        dismissedHints: [],
        seenPages: [],
        featureDiscoveries: [],

        // Transient defaults
        activeTour: null,
        activeTourStep: 0,
        isWelcomeOpen: false,
        activeHint: null,

        completeWelcome: (profile) =>
          set({
            hasCompletedWelcome: true,
            userProfile: profile,
            isWelcomeOpen: false,
          }),

        startTour: (tourId) =>
          set({
            activeTour: tourId,
            activeTourStep: 0,
          }),

        nextTourStep: () =>
          set((state) => ({
            activeTourStep: state.activeTourStep + 1,
          })),

        prevTourStep: () =>
          set((state) => ({
            activeTourStep: Math.max(0, state.activeTourStep - 1),
          })),

        skipTour: () =>
          set((state) => ({
            activeTour: null,
            activeTourStep: 0,
            completedTours: state.activeTour
              ? [...new Set([...state.completedTours, state.activeTour])]
              : state.completedTours,
          })),

        completeTour: () =>
          set((state) => ({
            activeTour: null,
            activeTourStep: 0,
            completedTours: state.activeTour
              ? [...new Set([...state.completedTours, state.activeTour])]
              : state.completedTours,
          })),

        dismissHint: (hintId) =>
          set((state) => ({
            dismissedHints: [...new Set([...state.dismissedHints, hintId])],
            activeHint: state.activeHint === hintId ? null : state.activeHint,
          })),

        markPageSeen: (path) =>
          set((state) => ({
            seenPages: state.seenPages.includes(path)
              ? state.seenPages
              : [...state.seenPages, path],
          })),

        discoverFeature: (featureId) =>
          set((state) => ({
            featureDiscoveries: [...new Set([...state.featureDiscoveries, featureId])],
          })),

        showHint: (hintId) => set({ activeHint: hintId }),

        hideHint: () => set({ activeHint: null }),

        resetOnboarding: () =>
          set({
            hasCompletedWelcome: false,
            userProfile: null,
            completedTours: [],
            dismissedHints: [],
            seenPages: [],
            featureDiscoveries: [],
            activeTour: null,
            activeTourStep: 0,
            isWelcomeOpen: false,
            activeHint: null,
          }),

        openWelcome: () => set({ isWelcomeOpen: true }),
      }),
      {
        name: 'envelop-onboarding',
        partialize: (state) => ({
          hasCompletedWelcome: state.hasCompletedWelcome,
          userProfile: state.userProfile,
          completedTours: state.completedTours,
          dismissedHints: state.dismissedHints,
          seenPages: state.seenPages,
          featureDiscoveries: state.featureDiscoveries,
        }),
      }
    ),
    { name: 'OnboardingStore' }
  )
);
